import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { AuthContextType, User, LoginRequest } from "../types/auth";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8080/api";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (token) {
      // Restore axios authorization header
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      // You might want to add a /api/auth/me endpoint to verify the token
      // and get fresh user data upon page load.
      const storedUser = sessionStorage.getItem("user");
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
    }
  }, []);

  const login = async (credentials: LoginRequest) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, credentials);
      const { token, user: loggedInUser } = response.data;

      // Store token and user data
      sessionStorage.setItem("token", token);
      sessionStorage.setItem("user", JSON.stringify(loggedInUser));

      // Set axios default header for subsequent requests
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      setUser(loggedInUser);
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  };

  const logout = () => {
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("user");
    delete axios.defaults.headers.common["Authorization"];
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
